import type { AppTheme } from './theme';

export type ServerUserPreferences = {
  userId: string;
  fullName: string;
  firstName?: string;
  familyName?: string;
  phone?: string;
  bio?: string;
  pictureUrl: string;
  timezone: string;
  theme: AppTheme;
  accessibilityDisplayEnabled: boolean;
  lastSeenAt?: string;
  updatedAt: string;
};

export type PreferenceSync = {
  apiUrl: string;
  accessToken?: string;
};

let cachedRequestKey = '';
let cachedRequest: Promise<ServerUserPreferences | null> | undefined;

export function loadServerPreferences(sync: PreferenceSync): Promise<ServerUserPreferences | null> {
  if (!sync.accessToken) return Promise.resolve(null);
  const requestKey = `${sync.apiUrl}\n${sync.accessToken}`;
  if (cachedRequestKey === requestKey && cachedRequest) return cachedRequest;
  cachedRequestKey = requestKey;
  cachedRequest = fetch(`${sync.apiUrl}/v1/me/preferences`, {
    headers: { authorization: `Bearer ${sync.accessToken}` },
    cache: 'no-store',
  })
    .then(async (response) => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const body = (await response.json()) as { preferences: ServerUserPreferences | null };
      return body.preferences;
    })
    .catch(() => null);
  return cachedRequest;
}

export async function saveServerPreferences(
  sync: PreferenceSync,
  update: Partial<
    Pick<
      ServerUserPreferences,
      | 'fullName'
      | 'firstName'
      | 'familyName'
      | 'phone'
      | 'bio'
      | 'pictureUrl'
      | 'timezone'
      | 'theme'
      | 'accessibilityDisplayEnabled'
    >
  >,
): Promise<ServerUserPreferences> {
  if (!sync.accessToken) throw new Error('Sign in again before changing your preferences.');
  const response = await fetch(`${sync.apiUrl}/v1/me/preferences`, {
    method: 'PATCH',
    headers: {
      authorization: `Bearer ${sync.accessToken}`,
      'content-type': 'application/json',
    },
    body: JSON.stringify(update),
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const preferences = (await response.json()) as ServerUserPreferences;
  cachedRequestKey = '';
  cachedRequest = undefined;
  return preferences;
}

export async function recordAppActivity(sync: PreferenceSync): Promise<void> {
  if (!sync.accessToken) return;
  // Let the initial preferences read settle first so a new activity record cannot
  // replace the OIDC-derived display name while the profile is initialising.
  await loadServerPreferences(sync);
  const response = await fetch(`${sync.apiUrl}/v1/me/activity`, {
    method: 'POST',
    headers: { authorization: `Bearer ${sync.accessToken}` },
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
}
